import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useHistory, useLocation } from "react-router-dom";
import useStore from "../../../context/useStore";
import useAllRecipients from "../../pages/AllRecipients/useAllRecipients";
import ApercuAllocataire from "../../pages/RecipientDetail/ApercuAllocataire";
import PanneauLateral from "../PanneauLateral/PanneauLateral";
import {
  CHEMIN_RECHERCHE_COMPLETE,
  detailSuggestion,
  estNumerique,
  estRaccourciRecherche,
  estRecherchable,
  filtresRechercheComplete,
  LONGUEUR_MAXIMALE,
  libelleRaccourci,
  messageEtat,
  nomComplet,
  segmentsSurlignes,
} from "./saisieEtSuggestions";
import useRechercheRapide from "./useRechercheRapide";
import "./recherche-rapide.scss";

// ============================================================
// Recherche rapide d'un allocataire, depuis n'importe quel écran : bouton
// rond du menu (rail, épinglé) ou de la barre haute (tiroir), et Ctrl / ⌘ K.
// Le bouton ouvre un champ d'autocomplétion (combobox WAI-ARIA : flèches,
// Entrée, Échap) par-dessus la page, à côté du menu ; une suggestion choisie
// ouvre l'aperçu de la fiche dans un panneau par la droite.
// Une seule instance, rendue par MenuLateral là où se trouve le bouton.
// ============================================================

const ID_DIALOGUE = "recherche-rapide";
const ID_LISTE = "recherche-rapide-liste";
const ID_ETAT = "recherche-rapide-etat";
const ID_PERIMETRE = "recherche-rapide-perimetre";
const idOption = (index) => `recherche-rapide-option-${index}`;
const FOCALISABLES = 'input, button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

// Une autre fenêtre modale occupe l'écran : le raccourci ne l'interrompt pas.
const autreDialogueOuvert = (state) =>
  Boolean(
    (state.modal && state.modal.isOpen) ||
      (state.modal2 && state.modal2.isOpen) ||
      (state.sidePanel && state.sidePanel.isOpen) ||
      (state.leftPanel && state.leftPanel.isOpen)
  );

const Surligne = ({ texte, saisie }) =>
  segmentsSurlignes(texte, saisie).map((segment, index) =>
    segment.surligne ? <mark key={index}>{segment.texte}</mark> : <React.Fragment key={index}>{segment.texte}</React.Fragment>
  );

// Position du champ : à droite du menu, à hauteur du bouton ; sous la barre
// haute en tiroir (la largeur y est fixée par la feuille de style). La
// hauteur s'arrête 12 px avant le bas de l'écran : la liste défile dedans.
function positionDepuis(bouton, variante) {
  if (!bouton) return null;
  const rect = bouton.getBoundingClientRect();
  const hauteurMax = (top) => Math.max(200, Math.round(window.innerHeight - top - 12));
  if (variante === "tiroir") {
    const barre = bouton.closest(".barre-haute");
    const top = Math.round((barre ? barre.getBoundingClientRect().bottom : rect.bottom) + 8);
    return { top, maxHeight: hauteurMax(top) };
  }
  const menu = bouton.closest(".menu-lateral");
  const droite = menu ? menu.getBoundingClientRect().right : rect.right;
  const top = Math.max(8, Math.round(rect.top - 4));
  return { top, left: Math.round(droite + 8), maxHeight: hauteurMax(top) };
}

const RechercheRapide = ({ variante = "rail" }) => {
  const [state] = useStore();
  const history = useHistory();
  const { pathname } = useLocation();
  const { onUpdateFilters, searchAllRecipients } = useAllRecipients();
  const [ouvert, setOuvert] = useState(false);
  const [apercu, setApercu] = useState(null);
  const [position, setPosition] = useState(null);
  const refBouton = useRef(null);
  const refChamp = useRef(null);
  const refDialogue = useRef(null);
  const raccourci = useMemo(() => libelleRaccourci(), []);

  const choisir = useCallback((allocataire) => {
    setOuvert(false);
    // Le focus passe au bouton avant l'ouverture du panneau : c'est là qu'il
    // reviendra à sa fermeture.
    if (refBouton.current) refBouton.current.focus();
    setApercu(allocataire);
  }, []);

  const recherche = useRechercheRapide({ onValidationDifferee: choisir });
  const { saisie, etat, allocataires, actif } = recherche;

  const ouvrir = useCallback(() => {
    setApercu(null);
    setOuvert(true);
  }, []);
  const fermer = useCallback(() => {
    setOuvert(false);
    if (refBouton.current) refBouton.current.focus();
  }, []);

  useLayoutEffect(() => {
    if (!ouvert) return undefined;
    const placer = () => setPosition(positionDepuis(refBouton.current, variante));
    placer();
    window.addEventListener("resize", placer);
    return () => window.removeEventListener("resize", placer);
  }, [ouvert, variante]);

  // À l'ouverture, le champ prend le focus et sa saisie précédente est
  // sélectionnée : taper la remplace, les flèches retrouvent les suggestions.
  useEffect(() => {
    if (ouvert && refChamp.current) {
      refChamp.current.focus();
      refChamp.current.select();
    }
  }, [ouvert]);

  // Un clic hors du champ et du bouton referme.
  useEffect(() => {
    if (!ouvert) return undefined;
    const surClic = (evenement) => {
      if (refDialogue.current && refDialogue.current.contains(evenement.target)) return;
      if (refBouton.current && refBouton.current.contains(evenement.target)) return;
      setOuvert(false);
    };
    document.addEventListener("mousedown", surClic);
    return () => document.removeEventListener("mousedown", surClic);
  }, [ouvert]);

  // Ctrl / ⌘ K, depuis n'importe quel écran.
  const occupe = autreDialogueOuvert(state);
  useEffect(() => {
    const surTouche = (evenement) => {
      if (!estRaccourciRecherche(evenement) || occupe) return;
      evenement.preventDefault();
      if (ouvert && refChamp.current) {
        refChamp.current.focus();
        refChamp.current.select();
      } else {
        ouvrir();
      }
    };
    document.addEventListener("keydown", surTouche);
    return () => document.removeEventListener("keydown", surTouche);
  }, [ouvert, occupe, ouvrir]);

  // Changer d'écran referme le champ et l'aperçu.
  useEffect(() => {
    setOuvert(false);
    setApercu(null);
  }, [pathname]);

  // L'option active reste visible quand la liste défile (écrans bas).
  useEffect(() => {
    const option = actif >= 0 ? document.getElementById(idOption(actif)) : null;
    if (option && option.scrollIntoView) option.scrollIntoView({ block: "nearest" });
  }, [actif]);

  // Échap referme d'où que soit le focus ; la tabulation tourne dans la
  // recherche (champ, croix, recherche complète) ; un clic à côté en sort.
  const surToucheDialogue = (evenement) => {
    if (evenement.key === "Escape") {
      evenement.preventDefault();
      evenement.stopPropagation();
      fermer();
      return;
    }
    if (evenement.key !== "Tab" || !refDialogue.current) return;
    const focalisables = Array.from(refDialogue.current.querySelectorAll(FOCALISABLES));
    if (!focalisables.length) return;
    const premier = focalisables[0];
    const dernier = focalisables[focalisables.length - 1];
    if (evenement.shiftKey && document.activeElement === premier) {
      evenement.preventDefault();
      dernier.focus();
    } else if (!evenement.shiftKey && document.activeElement === dernier) {
      evenement.preventDefault();
      premier.focus();
    }
  };

  const surToucheChamp = (evenement) => {
    switch (evenement.key) {
      case "ArrowDown":
        evenement.preventDefault();
        recherche.deplacer(1);
        break;
      case "ArrowUp":
        evenement.preventDefault();
        recherche.deplacer(-1);
        break;
      case "Enter": {
        evenement.preventDefault();
        const choisi = allocataires[actif];
        if (etat === "pret" && choisi) choisir(choisi);
        else if (etat === "chargement") recherche.validerDesQuePret();
        break;
      }
      default:
    }
  };

  // Si le focus part ailleurs (clic, autre écran), la recherche se referme.
  const surSortieDuFocus = (evenement) => {
    const suivant = evenement.relatedTarget;
    if (!suivant) return;
    if (refDialogue.current && refDialogue.current.contains(suivant)) return;
    if (refBouton.current && refBouton.current.contains(suivant)) return;
    setOuvert(false);
  };

  const lancerRechercheComplete = () => {
    const filtres = filtresRechercheComplete(saisie);
    if (!filtres) return;
    onUpdateFilters(filtres);
    searchAllRecipients(filtres);
    setOuvert(false);
    history.push(CHEMIN_RECHERCHE_COMPLETE);
  };

  const listeVisible = allocataires.length > 0;
  const numerique = estNumerique(saisie);
  const message = messageEtat({ etat, nombre: allocataires.length, plusDeResultats: recherche.plusDeResultats, saisie });

  const dialogue = ouvert
    ? createPortal(
        <div
          ref={refDialogue}
          id={ID_DIALOGUE}
          className={`recherche-rapide recherche-rapide--${variante}`}
          style={position || undefined}
          role="dialog"
          aria-label="Recherche rapide d'un allocataire"
          onBlur={surSortieDuFocus}
          onKeyDown={surToucheDialogue}
        >
          <div className="recherche-rapide__champ">
            <i className="fa fa-search recherche-rapide__loupe" aria-hidden="true" />
            <input
              ref={refChamp}
              type="text"
              className="form-control"
              role="combobox"
              aria-label="Nom, prénom, NIR ou matricule"
              aria-autocomplete="list"
              aria-expanded={listeVisible}
              aria-controls={ID_LISTE}
              aria-activedescendant={listeVisible && actif >= 0 ? idOption(actif) : undefined}
              aria-describedby={`${ID_PERIMETRE} ${ID_ETAT}`}
              placeholder="Nom, prénom, NIR ou matricule"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              enterKeyHint="search"
              maxLength={LONGUEUR_MAXIMALE}
              value={saisie}
              onChange={(e) => recherche.setSaisie(e.target.value)}
              onKeyDown={surToucheChamp}
            />
            {etat === "chargement" && <span className="recherche-rapide__attente" aria-hidden="true" />}
            {saisie && (
              <button
                type="button"
                className="recherche-rapide__vider"
                aria-label="Effacer la saisie"
                title="Effacer"
                onClick={() => {
                  recherche.setSaisie("");
                  if (refChamp.current) refChamp.current.focus();
                }}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* La route ne lit que la collection du flux : le dire avant les résultats. */}
          <p id={ID_PERIMETRE} className="recherche-rapide__perimetre">
            <i className="fa fa-info-circle" aria-hidden="true" />
            Allocataires du flux uniquement, hors temporaires et stock
          </p>

          <ul
            id={ID_LISTE}
            role="listbox"
            aria-label="Allocataires trouvés"
            className="recherche-rapide__liste"
            aria-busy={etat === "chargement" || undefined}
            hidden={!listeVisible}
          >
            {allocataires.map((allocataire, index) => {
              const detail = detailSuggestion(allocataire, { numerique });
              return (
                <li
                  key={allocataire.id}
                  id={idOption(index)}
                  role="option"
                  aria-selected={index === actif}
                  className="recherche-rapide__option"
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseMove={() => index !== actif && recherche.setActif(index)}
                  onClick={() => choisir(allocataire)}
                >
                  <span className="recherche-rapide__nom">
                    <Surligne texte={nomComplet(allocataire)} saisie={numerique ? "" : saisie} />
                  </span>
                  {detail && (
                    <span className="recherche-rapide__detail">
                      {numerique ? <Surligne texte={detail} saisie={saisie.replace(/\s/g, "")} /> : detail}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>

          <p id={ID_ETAT} className={`recherche-rapide__etat recherche-rapide__etat--${etat}`} role="status" aria-live="polite">
            {message}
            {etat === "erreur" && (
              <button type="button" className="btn btn-link recherche-rapide__reessayer" onClick={recherche.reessayer}>
                Réessayer
              </button>
            )}
          </p>

          <div className="recherche-rapide__pied">
            <button
              type="button"
              className="btn btn-link recherche-rapide__complete"
              title="Dans toutes les collections : flux, temporaires, stock et EP"
              onClick={lancerRechercheComplete}
              disabled={!estRecherchable(saisie)}
            >
              <i className="fa fa-search-plus" aria-hidden="true" />
              Recherche complète
            </button>
            <span className="recherche-rapide__aide" aria-hidden="true">
              <kbd>↑</kbd>
              <kbd>↓</kbd> choisir · <kbd>Entrée</kbd> ouvrir
            </span>
          </div>
        </div>,
        document.body
      )
    : null;

  const nomApercu = apercu ? nomComplet(apercu) : "";

  return (
    <>
      <button
        ref={refBouton}
        type="button"
        className={`recherche-rapide-fab recherche-rapide-fab--${variante}`}
        aria-label={`Recherche rapide d'un allocataire (${raccourci})`}
        aria-haspopup="dialog"
        aria-expanded={ouvert}
        aria-controls={ouvert ? ID_DIALOGUE : undefined}
        aria-keyshortcuts="Control+K Meta+K"
        data-priv="view_recipients"
        onClick={() => (ouvert ? fermer() : ouvrir())}
      >
        <i className="fa fa-search" aria-hidden="true" />
        {variante === "epingle" && (
          <>
            <span className="recherche-rapide-fab__libelle">Recherche rapide</span>
            <kbd className="recherche-rapide-fab__raccourci">{raccourci}</kbd>
          </>
        )}
        {variante === "rail" && (
          <span className="menu-lateral__bulle" aria-hidden="true">
            Recherche rapide · {raccourci}
          </span>
        )}
      </button>
      {dialogue}
      <PanneauLateral
        ouvert={!!apercu}
        onFermer={() => setApercu(null)}
        titre={`Aperçu de ${nomApercu || "la fiche allocataire"}`}
        surtitre="Aperçu de la fiche"
        className="panneau-apercu-allocataire"
        pied={
          apercu && (
            <>
              <button type="button" className="btn btn-ghost" onClick={ouvrir}>
                <i className="fa fa-search mr-2" aria-hidden="true" />
                Nouvelle recherche
              </button>
              <a
                className="btn btn-ghost btn-icon"
                href={`/recipients/${apercu.id}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ouvrir la fiche dans un nouvel onglet"
                title="Ouvrir dans un nouvel onglet"
              >
                <i className="fa fa-external-link-alt" aria-hidden="true" />
              </a>
              <Link className="btn btn-primary" to={`/recipients/${apercu.id}`}>
                Ouvrir la fiche
                <i className="fa fa-arrow-right ml-2" aria-hidden="true" />
              </Link>
            </>
          )
        }
      >
        {apercu && <ApercuAllocataire allocataireId={apercu.id} suggestion={apercu} />}
      </PanneauLateral>
    </>
  );
};

export default RechercheRapide;
